import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Card } from './ui/Card';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { Button } from './ui/Button';
import { Alert } from './ui/Alert';
import { SELECT_CLASS } from './AudiencePicker';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  contactCustomObjectsKey,
  customObjectErrorText,
  formatCustomValue,
  useContactCustomObjects,
} from '../lib/useCustomObjects';
import type { CustomField, CustomObjectRecord, CustomObjectType, CustomValue } from '../types/api';

/** Form state: every input is a string; booleans are 'true' / 'false' / ''. */
type FormValues = Record<string, string>;

function toForm(fields: CustomField[], values: Record<string, CustomValue>): FormValues {
  const out: FormValues = {};
  for (const f of fields) {
    const v = values[f.key];
    out[f.key] = v === undefined ? '' : String(v);
  }
  return out;
}

/** Empty inputs are left out, so the API reports a missing required field. */
function fromForm(fields: CustomField[], form: FormValues): Record<string, CustomValue> {
  const out: Record<string, CustomValue> = {};
  for (const f of fields) {
    const raw = (form[f.key] ?? '').trim();
    if (raw === '') continue;
    if (f.type === 'number') out[f.key] = Number(raw);
    else if (f.type === 'boolean') out[f.key] = raw === 'true';
    else out[f.key] = raw;
  }
  return out;
}

function FieldInput({
  id,
  field,
  value,
  onChange,
}: {
  id: string;
  field: CustomField;
  value: string;
  onChange: (v: string) => void;
}) {
  if (field.type === 'select' || field.type === 'boolean') {
    const options =
      field.type === 'boolean'
        ? [
            { v: 'true', l: 'Yes' },
            { v: 'false', l: 'No' },
          ]
        : (field.options ?? []).map((o) => ({ v: o, l: o }));
    return (
      <select
        id={id}
        className={SELECT_CLASS}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
    );
  }
  return (
    <Input
      id={id}
      type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
      step={field.type === 'number' ? 'any' : undefined}
      maxLength={field.type === 'text' ? 500 : undefined}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function RecordForm({
  type,
  contactId,
  record,
  onDone,
}: {
  type: CustomObjectType;
  contactId: string;
  record: CustomObjectRecord | null;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormValues>(() => toForm(type.fields, record?.values ?? {}));
  const save = useMutation({
    mutationFn: () => {
      const values = fromForm(type.fields, form);
      return record
        ? apiFetch<CustomObjectRecord>(`/custom-objects/records/${record.id}`, {
            method: 'PUT',
            body: { values },
          })
        : apiFetch<CustomObjectRecord>('/custom-objects/records', {
            method: 'POST',
            body: { typeId: type.id, contactId, values },
          });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactCustomObjectsKey(contactId) });
      onDone();
    },
  });
  const error = customObjectErrorText(save.error, 'The record could not be saved.');
  const prefix = `co-${type.id}-${record?.id ?? 'new'}`;

  return (
    <form
      className="border border-border-soft rounded-[10px] p-3 flex flex-col gap-3"
      aria-label={record ? `Edit ${type.name}` : `New ${type.name}`}
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
        {type.fields.map((f) => (
          <div key={f.key} className="flex flex-col gap-1.5 min-w-0">
            <Label htmlFor={`${prefix}-${f.key}`}>
              {f.label}
              {f.required ? <span className="text-muted"> (required)</span> : null}
            </Label>
            <FieldInput
              id={`${prefix}-${f.key}`}
              field={f}
              value={form[f.key] ?? ''}
              onChange={(v) => setForm((cur) => ({ ...cur, [f.key]: v }))}
            />
          </div>
        ))}
      </div>
      {error ? <Alert>{error}</Alert> : null}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={save.isPending}>
          {record ? 'Save' : `Add ${type.name.toLowerCase()}`}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function TypeSection({
  type,
  records,
  contactId,
}: {
  type: CustomObjectType;
  records: CustomObjectRecord[];
  contactId: string;
}) {
  const { isEditor } = useAuth();
  const queryClient = useQueryClient();
  /** 'new', a record id, or null. */
  const [open, setOpen] = useState<string | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => apiFetch(`/custom-objects/records/${id}`, { method: 'DELETE' }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: contactCustomObjectsKey(contactId) }),
  });
  const deleteError = customObjectErrorText(remove.error, 'The record could not be deleted.');
  const shown = type.fields.slice(0, 6);

  return (
    <Card className="p-4 flex flex-col gap-3" data-testid={`custom-object-section-${type.slug}`}>
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold m-0">{type.name}</h2>
        <span className="text-muted text-xs">{records.length}</span>
        {isEditor && open === null && type.fields.length > 0 ? (
          <Button size="sm" variant="secondary" className="ml-auto" onClick={() => setOpen('new')}>
            <Plus size={16} /> Add
          </Button>
        ) : null}
      </div>
      {open === 'new' ? (
        <RecordForm type={type} contactId={contactId} record={null} onDone={() => setOpen(null)} />
      ) : null}
      {deleteError ? <Alert>{deleteError}</Alert> : null}
      {records.length === 0 && open !== 'new' ? (
        <p className="text-muted text-sm m-0">No {type.name.toLowerCase()} records yet.</p>
      ) : null}
      {records.length > 0 ? (
        <ul className="m-0 p-0 list-none flex flex-col gap-2">
          {records.map((r) =>
            open === r.id ? (
              <li key={r.id}>
                <RecordForm
                  type={type}
                  contactId={contactId}
                  record={r}
                  onDone={() => setOpen(null)}
                />
              </li>
            ) : (
              <li
                key={r.id}
                className="border border-border-soft rounded-[10px] px-3 py-2 flex items-start gap-2"
              >
                <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm flex-1 min-w-0">
                  {shown.map((f) => (
                    <div key={f.key} className="contents">
                      <dt className="text-muted">{f.label}</dt>
                      <dd className="m-0 break-words">{formatCustomValue(r.values[f.key])}</dd>
                    </div>
                  ))}
                </dl>
                {isEditor ? (
                  <div className="flex gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Edit ${type.name} record`}
                      onClick={() => setOpen(r.id)}
                    >
                      <Pencil size={15} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Delete ${type.name} record`}
                      disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm(`Delete this ${type.name.toLowerCase()} record?`)) {
                          remove.mutate(r.id);
                        }
                      }}
                    >
                      <Trash2 size={15} />
                    </Button>
                  </div>
                ) : null}
              </li>
            ),
          )}
        </ul>
      ) : null}
    </Card>
  );
}

/**
 * The contact's custom object records (K8), one card per object type.
 * Renders nothing until an admin has created at least one type, so the
 * contact page looks the same as before for everyone else.
 */
export function ContactCustomObjects({ contactId }: { contactId: string }) {
  const { data } = useContactCustomObjects(contactId);
  const items = data?.items ?? [];
  if (items.length === 0) return null;
  return (
    <>
      {items.map((item) => (
        <TypeSection
          key={item.type.id}
          type={item.type}
          records={item.records}
          contactId={contactId}
        />
      ))}
    </>
  );
}
